import {
	Property,
	PropertyCard,
	Workspace,
	addChartToWorkspace,
	getWorkspaces,
	selectWorkspaces,
} from '@innovyze/stylovyze';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddToWorkspaceDialog from '../AddToWorkspaceDialog/AddToWorkspaceDialog';
import AddToWorkspaceIcon from '../../icons/AddToWorkspace';
import { FeatureItem } from '../../utils';
import HistoryMenuIcon from '../../icons/Historical';
import { SensorHistoryDialog } from '../SensorHistoryDialog';
import { SensorPropertiesWrapper } from './SensorProperties.styles';
import _ from 'lodash';
import { useGlobalization } from '@Translations/useGlobalization';

export interface SensorPropertiesProps {
	item: FeatureItem;
	addChartToCurrentWorkspace?: (sensorId: string) => void;
	currentWorkspaceId?: string;
}

export const SensorProperties = ({
	item,
	addChartToCurrentWorkspace,
	currentWorkspaceId,
}: SensorPropertiesProps): JSX.Element => {
	const { t } = useGlobalization();
	const properties: Property[] = [
		{
			title: t('Sensor ID', { context: 'Sensor properties' }) ?? 'Sensor ID',
			value: `${item.sensorId}`,
			ellipsis: true,
			cy: 'sensorId',
		},
		{
			title: t('Sensor Type', { context: 'Sensor properties' }) ?? 'Sensor Type',
			value: item.sensorType,
			ellipsis: true,
			cy: 'sensorType',
			loading: item.sensorType == null,
		},
	];

	const title = _.capitalize(`${item.alias || item.sensorId}`);

	const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
	const [workspaceModalOpen, setWorkspaceModalOpen] = useState<boolean>(
		false,
	);
	const closeHistoryModal = () => setHistoryModalOpen(false);
	const closeWorkspaceModal = () => setWorkspaceModalOpen(false);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getWorkspaces({}));
	}, []);
	const workspaces = useSelector(selectWorkspaces);
	const currentWorkspace = useMemo(
		() => workspaces?.find(({ id }) => id === currentWorkspaceId),
		[workspaces, currentWorkspaceId],
	);

	const addToWorkspace = (workspace: Workspace) => {
		const sensorId = item.id.split('#')[1];
		closeWorkspaceModal();
		if (currentWorkspaceId === workspace.id && addChartToCurrentWorkspace) {
			addChartToCurrentWorkspace(sensorId);
		} else {
			dispatch(
				addChartToWorkspace({
					workspaceId: workspace.id,
					sensorId,
				}),
			);
		}
	};

	return (
		<SensorPropertiesWrapper>
			<PropertyCard
				title={title}
				properties={properties}
				marginBottom
				menuItems={[
					{
						text: t('Historical Chart', {
							context: 'Sensor properties menu item',
						}) ?? 'Historical Chart',
						icon: <HistoryMenuIcon />,
						onClick: () => setHistoryModalOpen(true),
					},
					{
						text: t('Add chart to Workspace', {
							context: 'Sensor properties menu item',
						}) ?? 'Add chart to Workspace',
						icon: <AddToWorkspaceIcon />,
						onClick: () => setWorkspaceModalOpen(true),
					},
				]}
			/>
			<SensorHistoryDialog
				title={title}
				id={`${item.sensorId}`}
				alias={`${item.alias}`}
				modalOpen={historyModalOpen}
				closeModal={closeHistoryModal}
				setWorkspaceModalOpen={setWorkspaceModalOpen}
			/>
			<AddToWorkspaceDialog
				open={workspaceModalOpen}
				workspaces={workspaces}
				currentWorkspace={currentWorkspace}
				onCancel={closeWorkspaceModal}
				onConfirm={addToWorkspace}
			/>
		</SensorPropertiesWrapper>
	);
};
